import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { BrowserProvider, Contract, formatUnits } from "ethers";
import { CONTRACTS, SEPOLIA_CHAIN_ID } from "./contracts";
import { ERC20_ABI, SPORTFOLIO_IPO_ABI } from "./abis";
import { TEAM_LIST } from "./teams";

export const WalletContext = createContext(null);

const DISCONNECT_KEY = "sportfolio_wallet_disconnected";

function getEthereum() {
  return typeof window !== "undefined" ? window.ethereum : null;
}

function isManualDisconnect() {
  try {
    return sessionStorage.getItem(DISCONNECT_KEY) === "1";
  } catch {
    return false;
  }
}

function setManualDisconnect(on) {
  try {
    if (on) sessionStorage.setItem(DISCONNECT_KEY, "1");
    else sessionStorage.removeItem(DISCONNECT_KEY);
  } catch {
    // ignore
  }
}

export const WalletProvider = ({ children }) => {
  const ethereum = getEthereum();
  const [account, setAccount] = useState("");
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [usdcBalance, setUsdcBalance] = useState(null);
  const [teamHoldings, setTeamHoldings] = useState([]);
  const [error, setError] = useState("");

  const isSepolia = chainId === SEPOLIA_CHAIN_ID;

  const getProvider = useCallback(() => {
    if (!ethereum) return null;
    return new BrowserProvider(ethereum);
  }, [ethereum]);

  const refreshUsdcBalance = useCallback(
    async (addr) => {
      try {
        if (!addr) {
          setUsdcBalance(null);
          return;
        }
        const provider = getProvider();
        if (!provider) return;
        const network = await provider.getNetwork();
        const cid = Number(network.chainId);
        setChainId(cid);
        if (cid !== SEPOLIA_CHAIN_ID) {
          setUsdcBalance(null);
          return;
        }
        const usdc = new Contract(CONTRACTS.usdc, ERC20_ABI, provider);
        const bal = await usdc.balanceOf(addr);
        setUsdcBalance(bal);
      } catch {
        setUsdcBalance(null);
      }
    },
    [getProvider]
  );

  const refreshTeamHoldings = useCallback(
    async (addr) => {
      try {
        if (!addr) {
          setTeamHoldings([]);
          return;
        }
        const provider = getProvider();
        if (!provider) return;
        const network = await provider.getNetwork();
        const cid = Number(network.chainId);
        setChainId(cid);
        if (cid !== SEPOLIA_CHAIN_ID) {
          setTeamHoldings([]);
          return;
        }

        const ipo = new Contract(CONTRACTS.sportfolioIPO, SPORTFOLIO_IPO_ABI, provider);
        const balances = await Promise.all(
          TEAM_LIST.map(async (t) => {
            const [bal, supply] = await Promise.all([
              ipo.balanceOf(addr, t.tokenId),
              ipo.getTotalSupply(t.tokenId),
            ]);
            const balNum = Number(bal);
            const supplyNum = Number(supply);
            const ownershipPct = supplyNum > 0 ? (balNum / supplyNum) * 100 : 0;
            return {
              tokenId: t.tokenId,
              name: t.name,
              logo: t.logo,
              balance: bal,
              balanceNum: balNum,
              maxSupply: supply,
              ownershipPct,
            };
          })
        );

        setTeamHoldings(balances);
      } catch {
        setTeamHoldings([]);
      }
    },
    [getProvider]
  );

  const connect = useCallback(async () => {
    try {
      setError("");
      if (!ethereum) {
        setError("MetaMask not found.");
        return;
      }
      setIsConnecting(true);
      setManualDisconnect(false);
      const provider = new BrowserProvider(ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      const addr = accounts?.[0] || "";
      setAccount(addr);
      const network = await provider.getNetwork();
      setChainId(Number(network.chainId));
      await refreshUsdcBalance(addr);
      await refreshTeamHoldings(addr);
    } catch (e) {
      setError(e?.message || "Failed to connect wallet.");
    } finally {
      setIsConnecting(false);
    }
  }, [ethereum, refreshTeamHoldings, refreshUsdcBalance]);

  const disconnect = useCallback(() => {
    setManualDisconnect(true);
    setAccount("");
    setUsdcBalance(null);
    setTeamHoldings([]);
    setError("");
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!ethereum || isManualDisconnect()) return;
        const provider = new BrowserProvider(ethereum);
        const network = await provider.getNetwork();
        if (!mounted) return;
        setChainId(Number(network.chainId));

        const accounts = await provider.send("eth_accounts", []);
        const addr = accounts?.[0] || "";
        if (!mounted) return;
        if (!addr) return;
        setAccount(addr);
        await refreshUsdcBalance(addr);
        await refreshTeamHoldings(addr);
      } catch {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [ethereum, refreshTeamHoldings, refreshUsdcBalance]);

  useEffect(() => {
    if (!ethereum?.on) return;
    const onAccountsChanged = (accounts) => {
      if (isManualDisconnect()) return;
      const addr = accounts?.[0] || "";
      setAccount(addr);
      refreshUsdcBalance(addr);
      refreshTeamHoldings(addr);
    };
    const onChainChanged = (hexChainId) => {
      const cid = Number(hexChainId);
      setChainId(cid);
      if (!account) return;
      refreshUsdcBalance(account);
      refreshTeamHoldings(account);
    };
    ethereum.on("accountsChanged", onAccountsChanged);
    ethereum.on("chainChanged", onChainChanged);
    return () => {
      if (!ethereum?.removeListener) return;
      ethereum.removeListener("accountsChanged", onAccountsChanged);
      ethereum.removeListener("chainChanged", onChainChanged);
    };
  }, [account, ethereum, refreshTeamHoldings, refreshUsdcBalance]);

  const usdcBalanceDisplay = useMemo(() => {
    if (!account) return "";
    if (!isSepolia) return "";
    if (usdcBalance == null) return "—";
    return `$${Number(formatUnits(usdcBalance, 6)).toFixed(2)}`;
  }, [account, isSepolia, usdcBalance]);

  const holdingsSummary = useMemo(() => {
    if (!account) return "";
    if (!isSepolia) return "";
    const total = teamHoldings.reduce((acc, t) => acc + (t.balanceNum || 0), 0);
    return total.toLocaleString();
  }, [account, isSepolia, teamHoldings]);

  const value = useMemo(
    () => ({
      ethereumPresent: !!ethereum,
      account,
      chainId,
      isSepolia,
      isConnecting,
      error,
      connect,
      disconnect,
      getProvider,
      usdcBalance,
      usdcBalanceDisplay,
      refreshUsdcBalance,
      teamHoldings,
      refreshTeamHoldings,
      holdingsSummary,
    }),
    [
      account,
      chainId,
      connect,
      disconnect,
      error,
      ethereum,
      getProvider,
      isConnecting,
      isSepolia,
      refreshUsdcBalance,
      teamHoldings,
      refreshTeamHoldings,
      holdingsSummary,
      usdcBalance,
      usdcBalanceDisplay,
    ]
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};
